import * as Sentry from "@sentry/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { APP_VERSION } from "../constants/env";
import { getReleaseName } from "../legacy/utils/get-release-name";
import { registerChunkLoadRejectionRecovery } from "../lib/chunk-load-recovery/register-chunk-load-rejection-recovery";
import featureFlag from "../lib/feature-flag/feature-flag";
import { sentryConfig } from "../lib/sentry/sentryConfig";

const BOOT_SPLASH_ELEMENT_ID = "splash";

interface WrapperAppProps {
  readonly children: React.ReactNode;
}

export function InitializeApp({ children }: WrapperAppProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(function initializeApp() {
    let isMounted = true;

    window.moment = moment;
    window.moment.locale("it");
    window.year = window.moment().year();

    registerChunkLoadRejectionRecovery();

    if (!Sentry.isInitialized()) {
      Sentry.init(sentryConfig);
      const client = Sentry.getClient();
      if (client) {
        client.on("afterSendEvent", function notifyHotjarOnError(event) {
          if (event.level === "error" && window.hj) {
            window.hj("event", "error_occurred");
          }
        });
      }
    }

    Sentry.setTag("app_version", APP_VERSION);
    Sentry.setTag("release_name", getReleaseName());

    const promisesToInitializeApp = [
      import("../styles/index.css"),
      import("../lib/mixpanel/mixpanel.config"),
      import("../legacy/services/axios-config-singleton/axios-config-singleton"),
      // Fixes older browser compatibilities issues (https://github.com/remarkjs/react-markdown/issues/804)
      import("core-js/features/object" as any),
      import("../legacy/polyfill/promise"),
      import("../legacy/polyfill/resize-observer-polyfill"),
      featureFlag
        .initialize()
        .then(console.debug.bind(null, "Feature Flag initialized"))
        .catch(console.error.bind(null, "Feature Flag error:")),
    ];

    Promise.all(promisesToInitializeApp)
      .catch(console.error.bind(null, "Error initializing app"))
      .finally(function appIsReady() {
        if (isMounted) {
          setIsReady(true);
        }
      });

    return function cleanup() {
      isMounted = false;
    };
  }, []);

  useEffect(
    function removeBootSplash() {
      if (!isReady) {
        return;
      }
      document.getElementById(BOOT_SPLASH_ELEMENT_ID)?.remove();
    },
    [isReady]
  );

  if (!isReady) {
    return <></>;
  }

  return <>{children}</>;
}
